import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@src/lib/utils/cn";
import useUser from "@src/features/app/hooks/useUser";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType, Ff, OpusAgentPlaybook } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import { Button } from "@src/lib/ui/Button";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import Compose2Icon from "@src/lib/icons/18px/compose-2";
import AskOpusOrbIcon from "@src/features/assistantChat/AskOpusOrbIcon";
import {
  ASK_OPUS_ORB_KEY,
  useOrbStateStore,
  useDriveAskOpusOrb,
  useDriveOrb,
} from "@src/features/assistantChat/useAskOpusOrbState";
import { turnActivity, turnDidWork } from "./events";
import type { FinishBeat } from "./OpusAgentTurnMessage";

import { agentCapabilities } from "./agentCapabilities";
import AgentPanelHeaderDetails from "./AgentPanelHeaderDetails";
import ManagerAgentPanelHeaderDetails from "./ManagerAgentPanelHeaderDetails";
import {
  ManagerAgent,
  findManagerAgent,
  managerAgentAvatar,
  managerAgentPanelId,
  useManagerAgentChatId,
  useManagerAgentChatIds,
  useManagerAgentsStore,
} from "./managerAgents";
import { AgentBot } from "@src/features/agents/AgentAvatar";
import { OpusAgentFieldsFragment } from "./agentPanel.generated";
import { UseOpusAgentChatReturn, useOpusAgentChat } from "./useOpusAgentChat";
import { OpusAgentThread } from "./OpusAgentThread";
import { OpusAgentComposer } from "./OpusAgentComposer";
import {
  OPUS_AGENT_ATTACHMENT_EXTENSIONS,
  useOpusAgentAttachments,
} from "./attachments/useOpusAgentAttachments";
import {
  OpusAgentSendAttachment,
  SEND_GATE_FAILED,
} from "./attachments/sendGate";
import { OpusAgentChatHistoryButton } from "./OpusAgentChatHistoryButton";
import { OpusAgentSendWhileRunningDialog } from "./OpusAgentSendWhileRunningDialog";
import {
  DRAWER_MAX_WIDTH,
  DRAWER_MAX_WIDTH_CSS,
  DRAWER_MAX_VIEWPORT_FRACTION,
  DRAWER_MIN_WIDTH,
  clampDrawerWidth,
  effectiveDrawerWidth,
  maxDrawerWidthForViewport,
  useOpusAgentDrawerWidthStore,
} from "./useOpusAgentDrawerWidthStore";
import { useOpusAgentDrawerStore } from "./useOpusAgentDrawerStore";
import {
  ASK_OPUS_AGENT_ID,
  selectPanelAgent,
  useAgentPanel,
  useSetAgentArchived,
} from "./agentPanel";
import { useOpusAgentVoice } from "./voice/useOpusAgentVoice";
import {
  takeFirstMessageAttachments,
  takeFirstMessagePlaybooks,
} from "./firstMessagePlaybooks";
import { OpusAgentCanvasHost } from "./OpusAgentCanvas";
import { useCloseCanvasWithChat } from "./useOpusAgentCanvasStore";
import { useAutoOpenPlanCanvas } from "./useAutoOpenPlanCanvas";

// Ask Opus replies finish in place: the Thinking row's orb gathers its dots
// back and plays the turn's beat; the header, rail and top-nav orbs show it
// as one still frame.
const ASK_OPUS_FINISH_BEAT: FinishBeat = {
  renderIcon: (state) => <AskOpusOrbIcon size={36} state={state} />,
  onStart: (turnId, kind) =>
    useOrbStateStore.getState().playBeat(ASK_OPUS_ORB_KEY, turnId, kind),
};

const KEYBOARD_RESIZE_STEP = 16;
const PANEL_MIN_WIDTH_CSS = `min(${DRAWER_MIN_WIDTH}px, 28vw)`;

const focusComposer = (panel: HTMLElement | null) =>
  requestAnimationFrame(() =>
    panel
      ?.querySelector<HTMLElement>('[data-testid="chat-composer-input"]')
      ?.focus(),
  );

// Escape belongs to whatever is open inside the panel first: a menu, the
// history popover, a confirm dialog, or the message editor.
const escapeClosesPanel = (
  event: KeyboardEvent,
  panel: HTMLElement | null,
): boolean => {
  const target = event.target as HTMLElement;
  if (event.key !== "Escape" || event.defaultPrevented) return false;
  const layer = target.closest(
    '[role="menu"],[role="dialog"],[role="alertdialog"],[role="listbox"]',
  );
  if (layer && layer !== panel) return false;
  if (target.closest('[data-state="delayed-open"],[data-state="instant-open"]'))
    return false;
  if (target instanceof HTMLInputElement) return false;
  return (
    !(target instanceof HTMLTextAreaElement) ||
    target.dataset.testid === "chat-composer-input"
  );
};

type PendingQuery = {
  text: string;
  startNew: boolean;
  playbooks: OpusAgentPlaybook[] | null;
  attachments: OpusAgentSendAttachment[] | null;
};

// The agent panel: slides out from the left nav, beside the page content,
// showing Ask Opus (the unbound agent, with chat history) or one of the org's
// agents (one bound chat). It shares the Ask Opus open/initial-query store so
// the nav, the home hero and the settings opt-in keep working unchanged.
export const OpusAgentChat: React.FC = () => {
  const {
    isOpen,
    open,
    close,
    conversationId,
    initialQuery,
    clearInitialQuery,
  } = useChatSidebar(AiChatType.KnowledgeBase);
  const { gates, userId, selectedAgentId, selectedAgent, agentsLoading } =
    useAgentPanel();
  const killSwitchEnabled = useFeatureFlag(Ff.OpusAgentKillSwitch);
  // First render only after the persisted open state is restored below, so
  // a panel left open appears open instead of animating open on each load.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Opening or switching from a rail entry moves focus into the composer and
  // gives it back to that entry on close; other opens move no focus.
  const openerRef = useRef<HTMLElement | null>(null);

  // Agent-only persistence of the open state. On mount, a panel left open
  // reopens without a click; from then on every open/close writes through.
  // The shared store itself stays in-memory (the trainee KB drawer must keep
  // closing on reload). Under the kill switch the persisted value is ignored
  // entirely — neither hydrated (a disabled panel must not reopen itself
  // after every close) nor written; it resumes once the flag clears. A mount
  // under the flag counts as hydrated, so the flag clearing mid-session
  // never pops the panel open by itself.
  const openHydratedRef = useRef(false);
  useEffect(() => {
    if (killSwitchEnabled) {
      openHydratedRef.current = true;
      return;
    }
    if (!openHydratedRef.current) {
      openHydratedRef.current = true;
      if (useOpusAgentDrawerStore.getState().isOpen && !isOpen) {
        open();
        return;
      }
    }
    useOpusAgentDrawerStore.getState().setOpen(isOpen);
  }, [isOpen, open, killSwitchEnabled]);

  // The store's start-new-conversation intent is a fresh conversationId (the
  // KB runtime keys its session on it). The hero mints one before it sets
  // initialQuery; a query arriving under a conversationId this panel hasn't
  // consumed yet must start a new chat instead of continuing the selected
  // one. Held here, not in the per-agent body, so a hero submit while an
  // agent is showing switches to Ask Opus and still starts fresh. A query
  // already waiting when the panel mounts starts fresh too.
  const consumedConversationIdRef = useRef<string | null>(
    initialQuery ? null : conversationId,
  );
  const [pendingQuery, setPendingQuery] = useState<PendingQuery | null>(null);
  const consumePendingQuery = useCallback(() => setPendingQuery(null), []);
  useEffect(() => {
    if (!initialQuery || !initialQuery.trim()) return;
    const startNew = conversationId !== consumedConversationIdRef.current;
    consumedConversationIdRef.current = conversationId;
    const playbooks = takeFirstMessagePlaybooks();
    const attachments = takeFirstMessageAttachments();
    if (!killSwitchEnabled && gates.askOpus && userId) {
      selectPanelAgent(ASK_OPUS_AGENT_ID, userId);
      open();
      setPendingQuery({
        text: initialQuery.trim(),
        startNew,
        playbooks,
        attachments,
      });
    }
    clearInitialQuery();
  }, [
    initialQuery,
    conversationId,
    clearInitialQuery,
    killSwitchEnabled,
    gates.askOpus,
    userId,
    open,
  ]);

  useEffect(() => {
    if (!isOpen) {
      openerRef.current = null;
      return;
    }
    const active = document.activeElement as HTMLElement | null;
    openerRef.current = active?.dataset.agentPanelRow ? active : null;
    if (!openerRef.current) return;
    focusComposer(panelRef.current);
  }, [isOpen, selectedAgentId]);

  // Panel width: the persisted preference, overridden by the live value
  // while the right-edge handle is being dragged. Only the release commits.
  const storedWidth = useOpusAgentDrawerWidthStore((state) => state.width);
  const setStoredWidth = useOpusAgentDrawerWidthStore(
    (state) => state.setWidth,
  );
  const resetStoredWidth = useOpusAgentDrawerWidthStore(
    (state) => state.resetWidth,
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const width = dragWidth ?? storedWidth;
  // The panel sits on the left: dragging the handle RIGHT widens it.
  const renderedWidth = (): number =>
    Math.round(
      panelRef.current?.getBoundingClientRect().width ||
        effectiveDrawerWidth(width, window.innerWidth),
    );
  const widthFromPointer = (clientX: number): number => {
    const drag = dragRef.current;
    if (!drag) return width;
    return clampDrawerWidth(
      drag.startWidth + (clientX - drag.startX),
      maxDrawerWidthForViewport(window.innerWidth),
    );
  };
  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    // Start from the width actually on screen: a stored width the viewport
    // cap is clamping must not make the first pointer move jump.
    const startWidth = renderedWidth();
    dragRef.current = { startX: event.clientX, startWidth };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragWidth(startWidth);
  };
  const moveResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setDragWidth(widthFromPointer(event.clientX));
  };
  const endResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const committed = Math.min(
      widthFromPointer(event.clientX),
      renderedWidth(),
    );
    dragRef.current = null;
    setDragWidth(null);
    setStoredWidth(committed);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const keyResize = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === "ArrowRight"
        ? KEYBOARD_RESIZE_STEP
        : event.key === "ArrowLeft"
          ? -KEYBOARD_RESIZE_STEP
          : 0;
    if (!step) return;
    event.preventDefault();
    setStoredWidth(
      clampDrawerWidth(
        renderedWidth() + step,
        maxDrawerWidthForViewport(window.innerWidth),
      ),
    );
  };

  const shownWidth = effectiveDrawerWidth(width, window.innerWidth);

  const closePanel = () => {
    close();
    const opener = openerRef.current;
    openerRef.current = null;
    (opener?.isConnected && opener.offsetParent !== null
      ? opener
      : Array.from(
          document.querySelectorAll<HTMLElement>(
            `[data-agent-panel-row="${selectedAgentId}"]`,
          ),
        ).find((row) => row.offsetParent !== null)
    )?.focus();
  };

  if (!selectedAgentId || !mounted) return null;
  const isAskOpus = selectedAgentId === ASK_OPUS_AGENT_ID;
  const managerAgent = findManagerAgent(selectedAgentId);
  const active = isOpen && !killSwitchEnabled;

  const card = (
    <div className={OPUS_AGENT_PANEL_CARD_CLASS}>
      {isAskOpus ? (
        <AskOpusPanelBody
          active={active}
          killSwitchEnabled={killSwitchEnabled}
          userId={userId}
          pendingQuery={pendingQuery}
          onPendingQueryConsumed={consumePendingQuery}
          onClose={closePanel}
        />
      ) : managerAgent ? (
        <ManagerAgentPanelBody
          key={selectedAgentId}
          agent={managerAgent}
          active={active}
          killSwitchEnabled={killSwitchEnabled}
          userId={userId}
          onClose={closePanel}
        />
      ) : (
        <AgentPanelBody
          key={selectedAgentId}
          agentId={selectedAgentId}
          agent={selectedAgent}
          loading={agentsLoading}
          active={active}
          killSwitchEnabled={killSwitchEnabled}
          onClose={closePanel}
        />
      )}
    </div>
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={panelRef}
      className={cn(
        "overflow-hidden",
        // The open/close animation must not fight a drag.
        dragWidth === null &&
          "transition-[flex-basis,width,min-width] duration-300 ease-in-out motion-reduce:transition-none",
      )}
      // The stored width is a preference the panel shrinks from (down to its
      // minimum) before the page content drops below its own.
      style={{
        flex: `0 1 ${isOpen ? width : 0}px`,
        width: isOpen ? width : 0,
        minWidth: isOpen ? PANEL_MIN_WIDTH_CSS : 0,
        maxWidth: DRAWER_MAX_WIDTH_CSS,
      }}
      onKeyDown={(event) => {
        if (!escapeClosesPanel(event.nativeEvent, panelRef.current)) return;
        event.preventDefault();
        closePanel();
      }}
      inert={!isOpen}
      aria-hidden={!isOpen}
      aria-label="Agent chat"
      role="complementary"
      data-testid="opus-agent-drawer"
    >
      <div
        className="flex h-full min-w-0 py-2"
        style={{ width, maxWidth: `max(${PANEL_MIN_WIDTH_CSS}, 100%)` }}
      >
        {card}
        {/* The gap between the panel and the page is the resize handle.
            Double-click resets; arrow keys resize when focused. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat"
          // The range actually reachable here: on a narrow window the
          // viewport cap holds the panel under its usual minimum.
          aria-valuenow={shownWidth}
          aria-valuemin={Math.min(DRAWER_MIN_WIDTH, shownWidth)}
          aria-valuemax={Math.max(
            shownWidth,
            Math.min(
              DRAWER_MAX_WIDTH,
              Math.floor(window.innerWidth * DRAWER_MAX_VIEWPORT_FRACTION),
            ),
          )}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={isOpen ? 0 : -1}
          data-testid="opus-agent-drawer-resize-handle"
          className="w-2 shrink-0 cursor-col-resize touch-none rounded-sm transition-colors outline-none hover:bg-state-hover focus-visible:bg-state-hover active:bg-state-pressed"
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          onDoubleClick={resetStoredWidth}
          onKeyDown={keyResize}
        />
      </div>
    </div>
  );
};

// The panel's card: header, thread and composer (also drawn on /ui/agent).
export const OPUS_AGENT_PANEL_CARD_CLASS =
  "flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-bg-primary shadow-default";

const PanelHeader: React.FC<{
  icon?: React.ReactNode;
  title?: string;
  // Replaces the icon and title, e.g. an agent's clickable name.
  titleSlot?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  onClose: () => void;
}> = ({ icon, title, titleSlot, subtitle, actions, onClose }) => (
  <div className="flex min-h-12 items-center justify-between gap-2 border-b border-border-primary px-3">
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {titleSlot ?? (
        <>
          {icon}
          <Text
            variant="compact-title"
            ellipsis
            ellipsisParentFlexDirection="row"
          >
            {title}
          </Text>
        </>
      )}
      {subtitle ? (
        <Text variant="fine-print-description" className="text-text-tertiary">
          {subtitle}
        </Text>
      ) : null}
    </div>
    <div className="flex shrink-0 items-center gap-1.5">
      {actions}
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Button
            onClick={onClose}
            variant="secondary"
            size="icon-sm"
            className="ml-1 text-icon-secondary"
            aria-label="Close chat"
            data-testid="opus-agent-close"
          >
            <XIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Close</TooltipContent>
      </Tooltip>
    </div>
  </div>
);

export const KillSwitchNotice: React.FC = () => (
  <EmptyState className="py-8">
    <EmptyStateIcon>
      <AskOpusOrbIcon size={48} state="idle" still />
    </EmptyStateIcon>
    <EmptyStateTitle>Ask Opus is disabled</EmptyStateTitle>
    <EmptyStateDescription>
      We&apos;re making improvements. Ask Opus will be back online shortly.
    </EmptyStateDescription>
  </EmptyState>
);

// The Ask Opus panel's header: the orb and title, then new chat and
// history (hidden under the kill switch) and close.
export const AskOpusPanelHeader: React.FC<{
  killSwitchEnabled: boolean;
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onStartNewChat: () => void;
  onClose: () => void;
}> = ({
  killSwitchEnabled,
  activeChatId,
  onSelectChat,
  onStartNewChat,
  onClose,
}) => (
  <PanelHeader
    icon={<AskOpusOrbIcon size={20} still />}
    title="Primary Agent"
    onClose={onClose}
    actions={
      killSwitchEnabled ? null : (
        <>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                onClick={onStartNewChat}
                variant="ghost"
                size="icon-sm"
                aria-label="Start new chat"
                data-testid="opus-agent-new-chat"
              >
                <Compose2Icon />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Start new chat</TooltipContent>
          </Tooltip>
          <OpusAgentChatHistoryButton
            activeChatId={activeChatId}
            onSelectChat={onSelectChat}
          />
        </>
      )
    }
  />
);

// A bound agent's header: its bot and name (details, Edit, Archive),
// "Archived" with Unarchive for an archived agent, and close.
export const AgentPanelHeader: React.FC<{
  agentId: string;
  agent: OpusAgentFieldsFragment | null;
  unarchiving: boolean;
  onUnarchive: () => void;
  onClose: () => void;
}> = ({ agentId, agent, unarchiving, onUnarchive, onClose }) => {
  const archived = Boolean(agent?.archivedAt);
  return (
    <PanelHeader
      titleSlot={
        <AgentPanelHeaderDetails
          agentId={agentId}
          agent={agent}
          onUnarchive={onUnarchive}
        />
      }
      subtitle={archived ? "Archived" : undefined}
      onClose={onClose}
      actions={
        archived && agent ? (
          <Button
            variant="outline"
            size="xs"
            loading={unarchiving}
            onClick={onUnarchive}
            data-testid="agent-panel-unarchive"
          >
            Unarchive
          </Button>
        ) : null
      }
    />
  );
};

const AskOpusPanelBody: React.FC<{
  active: boolean;
  killSwitchEnabled: boolean;
  userId: string | null;
  pendingQuery: PendingQuery | null;
  onPendingQueryConsumed: () => void;
  onClose: () => void;
}> = ({
  active,
  killSwitchEnabled,
  userId,
  pendingQuery,
  onPendingQueryConsumed,
  onClose,
}) => {
  const canBuild = useFeatureFlag(Ff.OpusAgentWrites);
  // The manager agents' chats are Primary Agent chats too; Ask Opus's own
  // most recent chat skips them.
  const managerAgentChatIds = useManagerAgentChatIds(userId);
  // Kill switch on = zero background traffic, whenever it lands: no chat
  // lookup, no hydration, no status/events polling — an in-progress session
  // pauses in place and resumes when the switch lifts.
  const chat = useOpusAgentChat({
    active,
    userId,
    excludeChatIds: managerAgentChatIds,
  });
  const { startNewChat, sendMessage } = chat;
  // Every Ask Opus orb on the page follows this chat: the turn's current
  // face while it runs (thinking only while the model reasons), done for a
  // moment when it lands.
  const lastTurn = chat.turns.at(-1);
  useDriveAskOpusOrb(
    chat.isRunning,
    !!lastTurn && turnDidWork(lastTurn.events),
    lastTurn?.id ?? null,
    turnActivity(lastTurn?.events ?? []).orb,
  );

  useEffect(() => {
    if (!pendingQuery) return;
    if (pendingQuery.startNew) startNewChat();
    const { playbooks, attachments } = pendingQuery;
    if (playbooks || attachments)
      sendMessage(pendingQuery.text, {
        ...(playbooks ? { playbooks } : {}),
        ...(attachments ? { attachments } : {}),
      });
    else sendMessage(pendingQuery.text);
    onPendingQueryConsumed();
  }, [pendingQuery, startNewChat, sendMessage, onPendingQueryConsumed]);

  return (
    <>
      <AskOpusPanelHeader
        killSwitchEnabled={killSwitchEnabled}
        activeChatId={chat.activeChatId}
        onSelectChat={chat.selectChat}
        onStartNewChat={startNewChat}
        onClose={onClose}
      />
      {killSwitchEnabled ? (
        <KillSwitchNotice />
      ) : (
        <OpusAgentConversation
          chat={chat}
          active={active}
          canBuild={canBuild}
          finishBeat={ASK_OPUS_FINISH_BEAT}
          openerIcon={<AskOpusOrbIcon size={56} />}
        />
      )}
    </>
  );
};

const AgentPanelBody: React.FC<{
  agentId: string;
  agent: OpusAgentFieldsFragment | null;
  loading: boolean;
  active: boolean;
  killSwitchEnabled: boolean;
  onClose: () => void;
}> = ({ agentId, agent, loading, active, killSwitchEnabled, onClose }) => {
  const archived = Boolean(agent?.archivedAt);
  const chat = useOpusAgentChat({ active, agentId });
  const { setArchived, loading: unarchiving } = useSetAgentArchived();
  // The agent's bot follows its chat like the Ask Opus orb: the turn's
  // current face while it runs, held still in the nav row and header.
  const lastTurn = chat.turns.at(-1);
  useDriveOrb(
    agentId,
    chat.isRunning,
    !!lastTurn && turnDidWork(lastTurn.events),
    lastTurn?.id ?? null,
    turnActivity(lastTurn?.events ?? []).orb,
  );
  const avatar = useMemo(() => agent ?? { id: agentId }, [agent, agentId]);
  const finishBeat = useMemo<FinishBeat>(
    () => ({
      renderIcon: (state) => (
        <AgentBot agent={avatar} size={36} state={state} />
      ),
      onStart: (turnId, kind) =>
        useOrbStateStore.getState().playBeat(agentId, turnId, kind),
    }),
    [agentId, avatar],
  );
  return (
    <>
      <AgentPanelHeader
        agentId={agentId}
        agent={agent}
        unarchiving={unarchiving}
        onUnarchive={() => {
          if (agent) void setArchived(agent, false);
        }}
        onClose={onClose}
      />
      {killSwitchEnabled ? (
        <KillSwitchNotice />
      ) : (
        <OpusAgentConversation
          chat={chat}
          active={active}
          loading={loading}
          agentName={agent?.name}
          agentSuggestedQuestions={agent?.suggestedQuestions}
          agentDescription={agent?.template?.description}
          agentCapabilities={agentCapabilities(agent?.template?.key)}
          finishBeat={finishBeat}
          openerIcon={<AgentBot agent={avatar} size={56} />}
          unavailable={
            archived
              ? "This agent is archived. Unarchive it to keep chatting."
              : null
          }
        />
      )}
    </>
  );
};

// A manager agent (staff prototype): one Primary Agent chat per manager,
// as on mobile. The first open starts it with the agent's prompt; later
// opens continue it.
const ManagerAgentPanelBody: React.FC<{
  agent: ManagerAgent;
  active: boolean;
  killSwitchEnabled: boolean;
  userId: string | null;
  onClose: () => void;
}> = ({ agent, active, killSwitchEnabled, userId, onClose }) => {
  const panelId = managerAgentPanelId(agent.key);
  const storedChatId = useManagerAgentChatId(userId, agent.key);
  // Bound on mount; the hook follows its own chat from there.
  const [boundChatId] = useState(storedChatId ?? null);
  // The agent's prompt opens its chat: on the first open, or when the
  // remembered chat is gone.
  const promptPendingRef = useRef(boundChatId === null);
  const [promptRetry, setPromptRetry] = useState(0);
  // No userId: this chat must never become Ask Opus's persisted selection
  // (the hook also skips persisting for a bound chat).
  const chat = useOpusAgentChat({
    active,
    boundChatId,
    // Remembered from the send itself, so a switch away mid-send still
    // keeps the chat.
    onChatCreated: (chatId) => {
      if (userId)
        useManagerAgentsStore.getState().setChatId(userId, agent.key, chatId);
    },
    onBoundChatGone: () => {
      promptPendingRef.current = true;
      setPromptRetry((retry) => retry + 1);
    },
  });
  const { sendMessage } = chat;

  useEffect(() => {
    if (!promptPendingRef.current || !active || !userId) return;
    promptPendingRef.current = false;
    sendMessage(agent.prompt);
  }, [promptRetry, active, userId, sendMessage, agent.prompt]);

  const lastTurn = chat.turns.at(-1);
  useDriveOrb(
    panelId,
    chat.isRunning,
    !!lastTurn && turnDidWork(lastTurn.events),
    lastTurn?.id ?? null,
    turnActivity(lastTurn?.events ?? []).orb,
  );
  const avatar = useMemo(() => managerAgentAvatar(agent), [agent]);
  const finishBeat = useMemo<FinishBeat>(
    () => ({
      renderIcon: (state) => (
        <AgentBot agent={avatar} size={36} state={state} />
      ),
      onStart: (turnId, kind) =>
        useOrbStateStore.getState().playBeat(panelId, turnId, kind),
    }),
    [panelId, avatar],
  );
  return (
    <>
      <PanelHeader
        titleSlot={<ManagerAgentPanelHeaderDetails agent={agent} />}
        onClose={onClose}
      />
      {killSwitchEnabled ? (
        <KillSwitchNotice />
      ) : (
        <OpusAgentConversation
          chat={chat}
          active={active}
          agentName={agent.name}
          agentDescription={agent.summary}
          agentCapabilities={agent.capabilities}
          finishBeat={finishBeat}
          openerIcon={<AgentBot agent={avatar} size={56} />}
        />
      )}
    </>
  );
};

// The thread and composer for one chat (Ask Opus or a bound agent). Exported
// for /ui/agent, which feeds it a mock chat.
export const OpusAgentConversation: React.FC<{
  chat: UseOpusAgentChatReturn;
  active: boolean;
  loading?: boolean;
  agentName?: string | null;
  agentSuggestedQuestions?: readonly string[];
  agentDescription?: string | null;
  agentCapabilities?: readonly string[];
  canBuild?: boolean;
  openerIcon?: React.ReactNode;
  finishBeat?: FinishBeat;
  unavailable?: string | null;
}> = ({
  chat,
  active,
  loading = false,
  agentName,
  agentSuggestedQuestions,
  agentDescription,
  agentCapabilities,
  canBuild,
  openerIcon,
  finishBeat,
  unavailable,
}) => {
  const [input, setInput] = useState("");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const canSend = !unavailable;
  const userFirstName = useUser().user?.firstName;
  const voiceEnabled = useFeatureFlag(Ff.OpusAgentVoice);
  const voice = useOpusAgentVoice({
    turns: chat.turns,
    sendMessage: chat.sendMessage,
    abort: chat.abort,
  });
  // Closing the panel, or the chat becoming unavailable, hangs up the call.
  const { isActive: voiceActive, end: endVoice } = voice;
  useEffect(() => {
    if (voiceActive && (!active || !canSend)) endVoice();
  }, [voiceActive, active, canSend, endVoice]);

  const attachments = useOpusAgentAttachments();
  const [pendingSend, setPendingSend] = useState<string | null>(null);
  useEffect(() => {
    if (!chat.isRunning) setPendingSend(null);
  }, [chat.isRunning]);
  // Attachments ride in pill order; the gate keeps send closed until every
  // pill is ready, so the pills are all sendable here.
  const send = (message: string) => {
    const ready = attachments.sendable();
    if (ready.length) chat.sendMessage(message, { attachments: ready });
    else chat.sendMessage(message);
    setInput("");
    attachments.clear();
  };
  // Also reached from dictation's stop-and-send, which submits even when
  // nothing was heard: an empty draft sends nothing and keeps the pills.
  const handleSubmit = () => {
    if (!input.trim() || attachments.gate.blocked) return;
    if (chat.isRunning) {
      setPendingSend(input);
      return;
    }
    send(input);
  };
  const confirmPendingSend = () => {
    if (pendingSend === null) return;
    // The confirm button is disabled while blocked; this only guards a
    // programmatic confirm. The draft stays in the composer either way.
    if (!attachments.gate.blocked) send(pendingSend);
    setPendingSend(null);
  };

  // Pills belong to the chat they were attached in: an explicit switch to
  // another chat, or to a new one, drops them, uploads included. A new
  // chat's first send, or the first lookup resolving, is not a switch.
  const { clear: clearAttachments } = attachments;
  const selectionKeyRef = useRef(chat.selectionKey);
  useEffect(() => {
    if (selectionKeyRef.current === chat.selectionKey) return;
    selectionKeyRef.current = chat.selectionKey;
    clearAttachments();
  }, [chat.selectionKey, clearAttachments]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileMessageRef = useRef({ message: "", send: false });
  const [autoSend, setAutoSend] = useState<string | null>(null);
  const attachFiles = (message: string, send: boolean) => {
    fileMessageRef.current = { message, send };
    fileInputRef.current?.click();
  };
  const pickedFiles = (files: File[]) => {
    if (!files.length) return;
    const { message, send } = fileMessageRef.current;
    attachments.addFiles(files);
    setInput(message);
    setAutoSend(send ? message : null);
  };
  const { sendMessage } = chat;
  const { sendable, gate } = attachments;
  const pillCount = attachments.pills.length;
  useEffect(() => {
    if (autoSend === null) return;
    if (
      input !== autoSend ||
      !pillCount ||
      gate.reason === SEND_GATE_FAILED ||
      voiceActive
    )
      setAutoSend(null);
    else if (!gate.blocked && !chat.isRunning) {
      setAutoSend(null);
      sendMessage(autoSend, { attachments: sendable() });
      setInput("");
      clearAttachments();
    }
  }, [
    autoSend,
    input,
    pillCount,
    gate.reason,
    gate.blocked,
    voiceActive,
    chat.isRunning,
    sendMessage,
    sendable,
    clearAttachments,
  ]);

  useCloseCanvasWithChat({ selectionKey: chat.selectionKey, active });
  useAutoOpenPlanCanvas({
    chatId: chat.activeChatId,
    turns: chat.turns,
    loading: chat.loading,
    panelOpen: active,
  });

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col pr-1 pb-2">
      <OpusAgentCanvasHost
        chatId={chat.activeChatId}
        turns={chat.turns}
        onSendMessage={canSend ? chat.sendMessage : () => undefined}
        canSend={canSend}
      />
      <ScrollableFlexGrow
        className="min-w-0 flex-1 overflow-hidden"
        innerClassName="mx-auto w-full max-w-3xl min-w-0 pr-2 pl-3"
        innerStyle={{ overflowX: "hidden", overflowY: "auto" }}
        innerRef={scrollerRef}
      >
        <OpusAgentThread
          turns={chat.turns}
          loading={chat.loading || loading}
          loadFailed={chat.loadFailed}
          scrollerRef={scrollerRef}
          chatKey={chat.activeChatId}
          onRetryLoad={chat.retryLoad}
          onSendMessage={canSend ? chat.sendMessage : () => undefined}
          onAttachFiles={canSend && !voice.isActive ? attachFiles : undefined}
          onResumePolling={chat.resumePolling}
          onEditMessage={canSend ? chat.editMessage : undefined}
          agentName={agentName}
          // An archived agent can't send, so its starters would be dead buttons.
          agentSuggestedQuestions={canSend ? agentSuggestedQuestions : []}
          agentDescription={agentDescription}
          agentCapabilities={agentCapabilities}
          userFirstName={userFirstName}
          openerIcon={openerIcon}
          finishBeat={finishBeat}
          canBuild={canBuild}
          canSend={canSend}
        />
      </ScrollableFlexGrow>
      <div className="mx-auto w-full max-w-3xl pr-1 pl-2">
        {unavailable ? (
          <Text
            variant="compact-description"
            className="block py-4 text-center text-text-tertiary"
            multiline
            data-testid="agent-chat-unavailable"
          >
            {unavailable}
          </Text>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={OPUS_AGENT_ATTACHMENT_EXTENSIONS.join(",")}
              data-testid="opus-agent-answer-file-input"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = "";
                pickedFiles(files);
              }}
            />
            <OpusAgentComposer
              input={input}
              onInputChange={setInput}
              onSubmit={handleSubmit}
              isRunning={chat.isRunning}
              onStop={chat.abort}
              effectiveModel={chat.effectiveModel}
              defaultModel={chat.defaultModel}
              onSelectModel={chat.selectModel}
              voice={voiceEnabled ? voice : null}
              // A voice call sends without them: pills and the menu are
              // paused (hidden) while it is live, and back after.
              attachments={voice.isActive ? undefined : attachments}
            />
            <OpusAgentSendWhileRunningDialog
              open={pendingSend !== null && chat.isRunning}
              confirmDisabled={attachments.gate.blocked}
              confirmDisabledReason={attachments.gate.reason}
              onCancel={() => setPendingSend(null)}
              onConfirm={confirmPendingSend}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OpusAgentChat;
